import { Middleware } from 'redux';
import { State } from '../state';
import {
  sdDisposed,
  addNewResults,
  sdGetNextBatch,
  sdSetPollTimeout,
  loadStudentDataForDashboard,
  StudentResultsParams,
  startNewClassSession
} from '../actions/statistics';
import { ContentStore } from '../store';
import { ProblemGenerator, RawPageData, getProblemResults, ItsLoggerEvent, GetProblemResultsParams } from 'its-pages';
import { StudentResult } from '../../types/cards';
import { setCurrentClassroomToken } from '../actions/app-actions';
import { LOCAL_STORAGE_KEY, __userLocalStorageKey } from '../../constants/local-storage';
import { UILocation } from '../reducers/app-reducers';
import { getRoster } from '../../remote/lti';
import { setRoster } from '../actions/roster-actions';
import { v4 as uuidV4 } from 'uuid';

async function getNextResultBatch(store: ContentStore) {
  const {
    app: { currentClassroomEndTs: currentClassroomEndDate, currentClassroomToken: cid },
    statistics: { lastLogResultTime, tagsByCompetency }
  } = store.getState();

  // don't bother with a poll if there is no CID
  if (cid === '') {
    return;
  }

  store.dispatch(sdSetPollTimeout(undefined));

  let events: ItsLoggerEvent[] = [];

  // Use the classroom end date at the endpoint for the query if it is set AND if is not in the future.
  // (Using a future date is pointless, as there cannot be loki data to query)
  const currentTime = Date.now();
  const getResultParams: GetProblemResultsParams = {
    cid,
    start: lastLogResultTime,
    end: (currentClassroomEndDate && currentClassroomEndDate < currentTime) ?
      currentClassroomEndDate : currentTime
  };
  
  try {
    events = await getProblemResults(getResultParams);
  } catch {
    // let events fall through
  }

  // TODO:
  // If the loki stream returned fewer than the max limit of events per requests, that means
  // the most recent query retrieved all events in that time range.
  // In this case, that time range is complete and there is no need to fetch more data.
  // However, if we were at the limit, there might be more data to fetch, need to send another query.

  // If no events in the last request, no need to adjust the query range window.
  // Stop further requests.
  if (events.length < 1) {
    scheduleNextPoll(store);
    return;
  }

  let currentLogResultTime = lastLogResultTime;
  const results: StudentResult[] = events.map((event, index) => {
    const timestamp = event.timestamp;
    if (
      timestamp && timestamp > currentLogResultTime
    ) {
      currentLogResultTime = timestamp + 1;
    }
    // Generate a dummy id for each raw event (used if necessary to display individual examples)
    const dummyEventId = `${currentTime}=${index}`;
    return { id: dummyEventId, data: event.eventData as RawPageData };
  });

  const resultPayload: StudentResultsParams = { results, lastLogTime: currentLogResultTime };
  if (Object.keys(tagsByCompetency).length === 0) {
    const { skillDescriptions, tagDescriptions } = await ProblemGenerator.getFixedDescriptionData();
    resultPayload['skillDescriptions'] = skillDescriptions;
    resultPayload['skillIdToTags'] = tagDescriptions;
  }

  store.dispatch(addNewResults(resultPayload));
  store.dispatch(sdGetNextBatch());
}

function scheduleNextPoll(store: ContentStore) {
  const { isPollingEnabled, pollIntervalMs, pollTimeout } =
    store.getState().statistics;

  if (!isPollingEnabled  || pollTimeout !== undefined) {
    return;
  }

  const timeout = setTimeout(() => {
    store.dispatch(sdGetNextBatch());
  }, pollIntervalMs);

  store.dispatch(sdSetPollTimeout(timeout));
}

function terminatePoll(store: ContentStore) {
  const { pollTimeout } = store.getState().statistics;

  if (pollTimeout) {
    store.dispatch(sdSetPollTimeout(undefined));
    clearTimeout(pollTimeout);
  }
}

async function handleLoadNewClass(newClassToken: string, store: ContentStore) {
  const state = store.getState();
  const dispatch = store.dispatch;
  const {
    app: {
      currentCardType,
      location,
      currentClassroomStartTs: currentClassroomStartDate,
      ltiEnv,
      ltiModule,
      useFirebaseForEvents,
    }
  } = state;

  // All of the logic below is related to setting up additional data once a class loads.
  // It is what needs to be executed

  // Save to local browser storage the class code so it 'remembers' to load this one next time
  localStorage.setItem(
    __userLocalStorageKey(LOCAL_STORAGE_KEY.currentClassroomToken),
    newClassToken
  );
  localStorage.setItem(
    __userLocalStorageKey(LOCAL_STORAGE_KEY.currentStudentClassroomToken),
    newClassToken
  );

  dispatch(startNewClassSession({
    sessionId: uuidV4(),
    cid: newClassToken,
    logStartTsMs: currentClassroomStartDate ? currentClassroomStartDate : Date.now(),
  }));

  const isTeacher = location === UILocation.teacherHome;
  if (isTeacher) {
    if (ltiEnv) {
      const roster = await getRoster();
      dispatch(setRoster(roster));
    }

    dispatch(loadStudentDataForDashboard({
      isInitialData: true,
      useFirebase: useFirebaseForEvents,
      skillId: (ltiModule) ? ltiModule : undefined,
      cardType: currentCardType
    }));
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types
export const statisticsMiddleware: Middleware<{}, State> = (
  middlewareStore
) => {
  const contentStore = middlewareStore as ContentStore;

  return (next) => async (action) => {
    const prevState = contentStore.getState();
    const returnValue = next(action);

    if (setCurrentClassroomToken.match(action)) {
      const prevClassToken = prevState.app.currentClassroomToken;
      const newClassToken = action.payload;
      // If class has not changed a new session hasn't been set, don't try refetching class data
      if (prevClassToken !== newClassToken && newClassToken.length > 0) {
        handleLoadNewClass(newClassToken, contentStore);
      }
    } else if (loadStudentDataForDashboard.match(action)) {
      // If loading loki data, we are fetching everything.
      // Only do this for the initial load of a class.
      const { isInitialData, useFirebase } = action.payload;
      if (!useFirebase && isInitialData) {
        getNextResultBatch(contentStore);
      }
    } else if (sdDisposed.match(action)) {
      terminatePoll(contentStore);
    } else if (sdGetNextBatch.match(action)) {
      getNextResultBatch(contentStore);
    }

    return returnValue;
  };
};
